import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  $Table,
  $Button,
  $Space,
  $Popconfirm,
  $PageHeader,
  $Drawer,
} from "us.common/components";
import {
  FilePdfOutlined,
  ImportOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  DocumentsManagerHistoryColumns,
  EntityType,
  DocumentManagerColumKeys,
  DocumentType,
  DocumentItem,
} from "./constants";
import type { ColumnsType } from "antd/es/table";
import "./Home.scss";
import { getRouteUrl } from "us.helper";
import { documentsManagerHistory } from "us.collection.input-output/actions";
import { RootState } from "us.helper/types";
import { connect, ConnectedProps } from "react-redux";
import dayjs from "dayjs";
import { AssignDocContent } from "./components";

/**
@description
Documents Manager History - Home Component
Displays the history of mapped documents with preview and management actions.
Provides listing of previously mapped documents, deletion, and preview drawer.
Features:
- Fetch mapped documents on mount and on pagination
- Preview mapped document content in a drawer
- Delete single or multiple mapped documents
- Render category badges and entity type labels
@link https://unicorn-solutions.atlassian.net/browse/UPD-20
@author Keshan Nimnaka <keshana@unicorn-solutions.com>
@since 29/06/2026
*/
const DocumentsManagerHistory: React.FC<PropsFromRedux> = (props) => {
  const {
    getMappedCaseDocuments,
    assignedDocumentsList,
    deleteMappedDocument,
    getDocumentContent,
    isLoading,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [previewDrawer, setPreviewDrawer] = useState({
    visible: false,
    title: "",
  });
  const [hit, setHit] = useState(1);
  useEffect(() => {
    getMappedCaseDocuments &&
      getMappedCaseDocuments({
        hit: hit,
      });
  }, [hit]);
  const handleDelete = () => {
    if (deleteMappedDocument && selectedRowKeys && selectedRowKeys.length > 0) {
      const documentIds = selectedRowKeys.map((k) => Number(k));
      deleteMappedDocument({ documentIds });
      setSelectedRowKeys([]);
    }
  };

  const handleDeleteSingleLocal = (key: React.Key) => {
    if (deleteMappedDocument) {
      deleteMappedDocument({ documentIds: [Number(key)] });
    }
    setSelectedRowKeys((prev) => prev.filter((k) => k !== key));
  };
  const previewHandler = (doc: DocumentItem) => {
    if (!doc) return;
    getDocumentContent &&
      getDocumentContent({
        lookupId: doc.documentId,
        system: DocumentType.ENTITY_DOCUMENT,
        lookupType: doc.entityType,
      });
    setPreviewDrawer({
      title: doc.documentName,
      visible: true,
    });
  };
  const onPreviewDrawerClose = () => {
    setPreviewDrawer({
      title: "",
      visible: false,
    });
  };

const buildColumns = (
  constantsCols: any[],
  tLocal: any,
  onDeleteSingle: (k: React.Key) => void,
): ColumnsType<DocumentItem> => {
  const cols = constantsCols.map((c: any) => {
    const mapIndex: Record<string, string> = {
      documentName: DocumentManagerColumKeys.DOCUMENT_NAME,
      assignTo: DocumentManagerColumKeys.ASSIGNED_TO,
      documentCategory: DocumentManagerColumKeys.CATEGORY,
      mappedBy: DocumentManagerColumKeys.MAPPED_BY,
      mappedDate: DocumentManagerColumKeys.DATE,
      entityType: DocumentManagerColumKeys.TYPE,
    };

    const dataIndex =
      typeof c.dataIndex === "string"
        ? mapIndex[c.dataIndex] || c.dataIndex
        : c.dataIndex;

    const col: any = {
      ...c,
      title: c.title ? tLocal(c.title) : "",
      dataIndex,
      key: c.key || dataIndex,
    };

    if (c.dataIndex === DocumentManagerColumKeys.DOCUMENT_NAME) {
      col.render = (_: any, record: any) => (
        <$Space>
          <FilePdfOutlined className="mdh-file-icon" />
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              previewHandler(record);
            }}
          >
            {record.name || record.documentName}
          </a>
        </$Space>
      );
    }

    if (c.dataIndex === DocumentManagerColumKeys.DATE && !c.render) {
      col.width = 80;
      col.render = (_: string, record: any) =>
        dayjs(record.mappedDate).format("DD/MM/YYYY");
    }

    if (c.dataIndex === DocumentManagerColumKeys.CATEGORY) {
      col.render = (value: string) => (
        <span className="mdh-badge mdh-badge-default">{value}</span>
      );
    }

    if (c.dataIndex === DocumentManagerColumKeys.TYPE) {
      col.render = (value: string, record: any) => {
        const v = (record.entityType || value || "").toLowerCase();

        if (v === EntityType.CASE) return "Case";
        if (v === EntityType.SUBCASE) return "SubCase";
        if (v === EntityType.AR) return "AR";
        if (v === EntityType.CREDITOR) return "Creditor";

        return value || "";
      };
    }

    if (c.dataIndex === DocumentManagerColumKeys.ASSIGNED_TO) {
      col.width = 80;
      col.render = (value: string, record: any) => {
        const assignValue = record.assignTo || value;
        const entityType = (record.entityType || "").toLowerCase();

        let route = "";

        if (
          entityType === EntityType.CASE ||
          entityType === EntityType.SUBCASE
        ) {
          route = `/case/${assignValue}`;
        } else if (entityType === EntityType.AR) {
          route = `/ar/${assignValue}`;
        } else if (entityType === EntityType.CREDITOR) {
          route = `/creditor/${assignValue}`;
        }

        return (
          <a
            onClick={() => {
              if (route) {
                getRouteUrl.moduleRoute("collection", route);
              }
            }}
            style={{ fontSize: 12 }}
          >
            {assignValue}
          </a>
        );
      };
    }

    if (c.key === DocumentManagerColumKeys.ACTIONS) {
      col.ellipsis = false;
      col.width = 35;
      col.render = (_: any, record: any) => (
        <$Space>
          <$Popconfirm
            title={tLocal(
              "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER_HISTORY.SINGLE_DELETE_CONFIRM",
            )}
            onConfirm={() => onDeleteSingle(record.documentId)}
            okText={tLocal("US.COMMON:COMMON.YES")}
            cancelText={tLocal("US.COMMON:COMMON.NO")}
          >
            <$Button danger size="small" icon={<DeleteOutlined />} />
          </$Popconfirm>
        </$Space>
      );
    }

    return col;
  });

  return cols as ColumnsType<DocumentItem>;
};

  const columns = buildColumns(
    DocumentsManagerHistoryColumns,
    t ?? null,
    handleDeleteSingleLocal,
  ) as ColumnsType<DocumentItem>;

  return (
    <div className="manage-documents-history mdh-container">
      <div className="page-header header-border pr-0 d-flex flex-row align-items-center justify-content-between mdh-header">
        <$PageHeader
          className="site-page-header"
          title={t(
            "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER_HISTORY.DOCUMENT_MANAGEMENT",
          )}
        ></$PageHeader>
        <$Space>
          {selectedRowKeys.length > 1 && (
            <$Popconfirm
              title={t(
                "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER_HISTORY.MULTIPLE_DELETE_CONFIRM",
              )}
              onConfirm={handleDelete}
              okText={t("US.COMMON:COMMON.YES")}
              cancelText={t("US.COMMON:COMMON.NO")}
            >
              <$Button danger data-testid="delete-selected">
                {t(
                  "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER_HISTORY.DELETE_ALL",
                )}
              </$Button>
            </$Popconfirm>
          )}

          <$Button
            icon={<ImportOutlined />}
            onClick={() => navigate("/import-and-process-documents")}
          >
            {t(
              "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER_HISTORY.IMPORT_AND_PROCESS_DOCUMENTS",
            )}
          </$Button>
        </$Space>
      </div>
      <div className="mdh-table-wrap">
        <$Table
          rowKey="documentId"
          dataSource={
            Array.isArray(assignedDocumentsList) ? assignedDocumentsList : []
          }
          className="mt-3"
          columns={columns}
          pagination={false}
          bordered={false}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            preserveSelectedRowKeys: true,
          }}
        />

        {Array.isArray(assignedDocumentsList) &&
          assignedDocumentsList.length > 50 && (
            <div className="mdh-load-more-wrap">
              <$Button
                onClick={() => {
                  setHit(hit + 1);
                }}
                disabled={isLoading}
              >
                {" "}
                {t(
                  "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER_HISTORY.LOAD_MORE",
                )}
              </$Button>
            </div>
          )}
      </div>
      <$Drawer
        title={previewDrawer.title}
        width="75%"
        placement="right"
        onClose={onPreviewDrawerClose}
        className="case-doc-viwer"
        open={previewDrawer.visible}
        destroyOnClose
      >
        <AssignDocContent onClose={onPreviewDrawerClose} />
      </$Drawer>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const { documentsManagerHistory } = state;
  const { assignedDocumentsList } = documentsManagerHistory;
  const { data, isLoading } = assignedDocumentsList || {};
  return { assignedDocumentsList: data, isLoading };
};

const mapDispatchToProps = {
  getMappedCaseDocuments: documentsManagerHistory.assignedDocuments.get,
  deleteMappedDocument: documentsManagerHistory.assignedDocuments.delete,
  getDocumentContent: documentsManagerHistory.documentContent.get,
};
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DocumentsManagerHistory);
